import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from "../AuthContext";
import { db } from '../firebase';
import { doc, getDoc, collection, addDoc, getDocs, query, updateDoc, setDoc } from 'firebase/firestore';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Grid2 as Grid,
  Fade,
  LinearProgress,
  Stack,
} from '@mui/material';
import { styled } from '@mui/system';
import YouTubeIcon from '@mui/icons-material/YouTube';
import QuizIcon from '@mui/icons-material/Quiz';
import ListAltIcon from '@mui/icons-material/ListAlt';
import EditIcon from '@mui/icons-material/Edit';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description'; // Icon for solutions link
import defaultResourceImage from '../assets/MATHSGEM.png';
import MathGemLogoMain from '../assets/MathGemLogoMain.png';

// Modern color palette
const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  background: '#f5f7fa',
  accent: '#3498db',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
};

// Styled components
const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  background: 'white',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const ChipStyled = styled(Chip)(({ theme }) => ({
  borderRadius: '24px',
  padding: theme.spacing(0.75, 1.5),
  fontWeight: '600',
  fontSize: '0.9rem',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: colors.accent,
    color: 'white',
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1.25, 2.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: colors.accent,
    color: 'white',
    '& svg': {
      transform: 'translateX(6px)',
    },
  },
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 450 },
  maxWidth: '100%',
  backgroundColor: 'white',
  borderRadius: { xs: '8px', sm: '12px' },
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
  padding: { xs: theme.spacing(2), sm: theme.spacing(4) },
  maxHeight: '90vh',
  overflowY: 'auto',
}));

// Function to extract YouTube video ID and generate thumbnail URL
const getVideoThumbnail = (url) => {
  try {
    const urlObj = new URL(url);
    let videoId = null;

    if (urlObj.hostname.includes('youtube.com') && urlObj.searchParams.get('v')) {
      videoId = urlObj.searchParams.get('v');
    } else if (urlObj.hostname.includes('youtu.be')) {
      videoId = urlObj.pathname.split('/')[1];
    }

    return videoId ? `https://img.youtube.com/vi/${videoId}/0.jpg` : null;
  } catch (error) {
    console.error('Invalid URL:', error);
    return null;
  }
};

const ViewTopic = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [topic, setTopic] = useState(null);
  const [resources, setResources] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [schoolLevels, setSchoolLevels] = useState([]);
  const [viewedResources, setViewedResources] = useState([]);
  const [newResource, setNewResource] = useState({ type: '', link: '', title: '', description: '', solutionsLink: '' });
  const [editResource, setEditResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedType, setSelectedType] = useState('video');

  const resourceTypes = [
    { value: 'video', label: 'Video (YouTube or similar link)' },
    { value: 'quiz', label: 'Quiz (Google Forms link)' },
    { value: 'worksheet', label: 'Worksheet (Google Drive PDF link)' },
  ];

  useEffect(() => {
    const fetchTopicData = async () => {
      setLoading(true);
      setError(null);
      try {
        const topicRef = doc(db, 'topics', topicId);
        const topicSnap = await getDoc(topicRef);
        if (!topicSnap.exists()) throw new Error('Topic not found');
        setTopic({ id: topicSnap.id, ...topicSnap.data() });

        const resourcesCollection = collection(db, `topics/${topicId}/resources`);
        const resourcesSnap = await getDocs(query(resourcesCollection));
        const resourcesData = resourcesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setResources(resourcesData);

        const subjectsSnap = await getDocs(collection(db, 'subjects'));
        setSubjects(subjectsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const schoolLevelsSnap = await getDocs(collection(db, 'schoolLevels'));
        setSchoolLevels(schoolLevelsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        if (user) {
          const viewedCollection = collection(db, `users/${user.uid}/viewedResources`);
          const viewedSnap = await getDocs(query(viewedCollection));
          const viewedData = viewedSnap.docs
            .filter(doc => doc.data().topicId === topicId)
            .map(doc => doc.data().resourceId);
          setViewedResources(viewedData);
        }
      } catch (err) {
        setError('Failed to load topic or resources. Please try again.');
        console.error('Error fetching topic data:', err);
        if (err.message === 'Topic not found') navigate('/topics');
      } finally {
        setLoading(false);
      }
    };

    fetchTopicData();
  }, [topicId, navigate, user]);

  const markResourceAsViewed = async (resourceId) => {
    if (!user || viewedResources.includes(resourceId)) return;

    try {
      const viewedDocRef = doc(db, `users/${user.uid}/viewedResources`, `${topicId}_${resourceId}`);
      await setDoc(viewedDocRef, {
        topicId,
        resourceId,
        viewedAt: new Date(),
      });
      setViewedResources([...viewedResources, resourceId]);
    } catch (err) {
      console.error('Error marking resource as viewed:', err);
    }
  };

  const handleChange = (e) => {
    setNewResource({ ...newResource, [e.target.name]: e.target.value });
  };

  const handleEditChange = (e) => {
    setEditResource({ ...editResource, [e.target.name]: e.target.value });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!user || user.role !== 'admin') return alert('Only admins can add resources.');
    if (!newResource.type || !newResource.link || !newResource.title || !newResource.description) {
      return alert('Please fill in all required fields.');
    }

    try {
      const resourcesCollection = collection(db, `topics/${topicId}/resources`);
      const docRef = await addDoc(resourcesCollection, {
        ...newResource,
        createdAt: new Date(),
        addedBy: user.uid,
      });

      setResources([...resources, { id: docRef.id, ...newResource, createdAt: new Date(), addedBy: user.uid }]);
      setNewResource({ type: '', link: '', title: '', description: '', solutionsLink: '' });
      setOpenAddModal(false);
    } catch (err) {
      console.error('Error adding resource:', err);
      alert('Failed to add resource.');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!user || user.role !== 'admin') return alert('Only admins can edit resources.');
    if (!editResource.type || !editResource.link || !editResource.title || !editResource.description) {
      return alert('Please fill in all required fields.');
    }

    try {
      const resourceRef = doc(db, `topics/${topicId}/resources`, editResource.id);
      await updateDoc(resourceRef, { ...editResource, updatedAt: new Date() });

      setResources(resources.map(r => (r.id === editResource.id ? { ...r, ...editResource, updatedAt: new Date() } : r)));
      setEditResource(null);
      setOpenEditModal(false);
    } catch (err) {
      console.error('Error updating resource:', err);
      alert('Failed to update resource.');
    }
  };

  const handleOpenAddModal = () => user?.role === 'admin' ? setOpenAddModal(true) : alert('Only admins can add resources.');
  const handleOpenEditModal = (resource) => user?.role === 'admin' ? (setEditResource(resource) || setOpenEditModal(true)) : alert('Only admins can edit resources.');
  const handleCloseAddModal = () => setOpenAddModal(false);
  const handleCloseEditModal = () => setOpenEditModal(false);

  const handleChipClick = (type) => setSelectedType(type);
  const filteredResources = resources.filter(r => r.type === selectedType);

  const totalResources = resources.length;
  const viewedCount = viewedResources.length;
  const viewedPercentage = totalResources > 0 ? Math.round((viewedCount / totalResources) * 100) : 0;

  if (loading) {
    return (
      <div style={{ background: '#f5f7fa' }}>
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: colors.background,
          }}
        >
          <img src={MathGemLogoMain} width={350} alt="Math Gem Logo" />
          <Fade in={true} timeout={600}>
            <LinearProgress
              sx={{ color: colors.primary, mb: 2 }}
              size={60}
              thickness={4}
            />
          </Fade>
          <Typography variant="h6" sx={{ color: colors.secondary }}>
            Loading your topic...
          </Typography>
        </Container>
      </div>
    );
  }

  if (!topic) return <Typography sx={{ textAlign: 'center', color: colors.secondary }}>Topic not found.</Typography>;

  const subjectName = subjects.find(s => s.id === topic.subjectId)?.name || 'N/A';
  const schoolLevelName = schoolLevels.find(sl => sl.id === topic.schoolLevelId)?.name || 'N/A';

  return (
    <div style={{ background: '#f5f7fa' }}>
      <Container sx={{ py: 8, minHeight: '100vh', background: colors.background, mt: 5 }}>
        <Paper
          sx={{
            p: 4,
            mb: 5,
            borderRadius: '16px',
            background: `linear-gradient(159deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
            color: 'white',
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: '700', mb: 1 }}>
            {topic.name}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {topic.description}
          </Typography>
          <Typography variant="body2">
            <strong>Subject:</strong> {subjectName}
          </Typography>
          <Typography variant="body2">
            <strong>School Level:</strong> {schoolLevelName}
          </Typography>
          <Typography variant="body2">
            <strong>Progress:</strong> {viewedCount} of {totalResources} resources viewed ({viewedPercentage}%)
          </Typography>
        </Paper>

        {error && (
          <Typography sx={{ textAlign: 'center', color: '#e74c3c', mb: 4, fontWeight: '500' }}>
            {error}
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'center', sm: 'space-between' },
            alignItems: 'center',
            mb: 4,
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            sx={{
              alignItems: 'center',
              flexWrap: { sm: 'wrap' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <ChipStyled
              label="Videos"
              icon={<YouTubeIcon />}
              color="primary"
              variant={selectedType === 'video' ? 'filled' : 'outlined'}
              onClick={() => handleChipClick('video')}
              sx={{
                bgcolor: selectedType === 'video' ? colors.primary : 'transparent',
                color: selectedType === 'video' ? 'white' : colors.secondary,
                minWidth: { xs: '150px', sm: 'auto' },
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                padding: { xs: '6px 12px', sm: '8px 16px' },
              }}
            />
            <ChipStyled
              label="Quizzes"
              icon={<QuizIcon />}
              color="primary"
              variant={selectedType === 'quiz' ? 'filled' : 'outlined'}
              onClick={() => handleChipClick('quiz')}
              sx={{
                bgcolor: selectedType === 'quiz' ? colors.primary : 'transparent',
                color: selectedType === 'quiz' ? 'white' : colors.secondary,
                minWidth: { xs: '150px', sm: 'auto' },
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                padding: { xs: '6px 12px', sm: '8px 16px' },
              }}
            />
            <ChipStyled
              label="Worksheets"
              icon={<ListAltIcon />}
              color="primary"
              variant={selectedType === 'worksheet' ? 'filled' : 'outlined'}
              onClick={() => handleChipClick('worksheet')}
              sx={{
                bgcolor: selectedType === 'worksheet' ? colors.primary : 'transparent',
                color: selectedType === 'worksheet' ? 'white' : colors.secondary,
                minWidth: { xs: '150px', sm: 'auto' },
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                padding: { xs: '6px 12px', sm: '8px 16px' },
              }}
            />
          </Stack>
          {user?.role === 'admin' && (
            <AnimatedButton
              variant="contained"
              color="primary"
              onClick={handleOpenAddModal}
              sx={{
                mt: { xs: 2, sm: 0 },
                width: { xs: '150px', sm: 'auto' },
                fontSize: { xs: '0.85rem', sm: '1rem' },
              }}
            >
              Add Resource
            </AnimatedButton>
          )}
        </Box>

        <Fade in={true} timeout={400}>
          <Grid container spacing={3}>
            {filteredResources.length > 0 ? (
              filteredResources.map((resource) => {
                const thumbnail = resource.type === 'video' ? getVideoThumbnail(resource.link) : null;
                const displayImage = thumbnail || defaultResourceImage;
                const isViewed = viewedResources.includes(resource.id);

                return (
                  <Grid key={resource.id} size={{ xs: 12, sm: 6, md: 4 }}>
                    <ModernCard>
                      {resource.type === 'video' && (
                        <CardMedia
                          component="img"
                          height="180"
                          image={displayImage}
                          alt={thumbnail ? 'Video Thumbnail' : 'Default Resource Image'}
                          sx={{ borderRadius: '12px 12px 0 0' }}
                        />
                      )}
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: '600', color: colors.secondary }}>
                          {resource.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                          {resource.description}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <AnimatedButton
                            href={resource.link}
                            target="_blank"
                            variant="outlined"
                            color="primary"
                            endIcon={<ArrowCircleRightIcon />}
                            onClick={() => markResourceAsViewed(resource.id)}
                          >
                            View {resource.type}
                          </AnimatedButton>
                          {resource.type === 'worksheet' && resource.solutionsLink && (
                            <AnimatedButton
                              href={resource.solutionsLink}
                              target="_blank"
                              variant="outlined"
                              color="secondary"
                              endIcon={<DescriptionIcon />}
                            >
                              Solutions
                            </AnimatedButton>
                          )}
                          {isViewed && (
                            <Chip
                              label="Viewed"
                              icon={<CheckCircleIcon />}
                              color="success"
                              size="small"
                              sx={{ bgcolor: colors.primary, color: 'white' }}
                            />
                          )}
                          {user?.role === 'admin' && (
                            <AnimatedButton
                              variant="outlined"
                              color="secondary"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenEditModal(resource)}
                            >
                              Edit
                            </AnimatedButton>
                          )}
                        </Box>
                      </CardContent>
                    </ModernCard>
                  </Grid>
                );
              })
            ) : (
              <Grid size={12}>
                <Typography sx={{ textAlign: 'center', color: colors.secondary }}>
                  No {selectedType === 'video' ? 'videos' : selectedType === 'quiz' ? 'quizzes' : 'worksheets'} available yet.
                </Typography>
              </Grid>
            )}
          </Grid>
        </Fade>

        {/* Add Resource Modal */}
        <Modal open={openAddModal} onClose={handleCloseAddModal}>
          <ModalBox sx={{padding:3, borderRadius: 5}}>
            <Typography
              variant="h5"
              sx={{ fontWeight: '700', color: colors.secondary, mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            >
              Add New Resource
            </Typography>
            <form onSubmit={handleAddSubmit}>
              <FormControl fullWidth sx={{ mb: { xs: 1.5, sm: 2 } }}>
                <InputLabel>Resource Type</InputLabel>
                <Select
                  name="type"
                  value={newResource.type}
                  onChange={handleChange}
                  required
                  sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                >
                  <MenuItem value=""><em>Select a type</em></MenuItem>
                  {resourceTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Title"
                name="title"
                value={newResource.title}
                onChange={handleChange}
                sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.9rem', sm: '1rem' } }}
                required
              />
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={newResource.description}
                onChange={handleChange}
                sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.9rem', sm: '1rem' } }}
                required
              />
              <TextField
                fullWidth
                label="Link"
                name="link"
                value={newResource.link}
                onChange={handleChange}
                sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.9rem', sm: '1rem' } }}
                required
              />
              {newResource.type === 'worksheet' && (
                <TextField
                  fullWidth
                  label="Solutions Link (Optional)"
                  name="solutionsLink"
                  value={newResource.solutionsLink}
                  onChange={handleChange}
                  sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.9rem', sm: '1rem' } }}
                />
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 2 },
                  justifyContent: { sm: 'flex-start' },
                }}
              >
                <AnimatedButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth={{ xs: true, sm: false }}
                  sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}
                >
                  Add
                </AnimatedButton>
                <AnimatedButton
                  variant="outlined"
                  color="secondary"
                  onClick={handleCloseAddModal}
                  fullWidth={{ xs: true, sm: false }}
                  sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}
                >
                  Cancel
                </AnimatedButton>
              </Box>
            </form>
          </ModalBox>
        </Modal>

        {/* Edit Resource Modal */}
        <Modal open={openEditModal} onClose={handleCloseEditModal}>
          <ModalBox  sx={{padding:3, borderRadius: 5}}>
            <Typography
              variant="h5"
              sx={{ fontWeight: '700', color: colors.secondary, mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            >
              Edit Resource
            </Typography>
            {editResource && (
              <form onSubmit={handleEditSubmit}>
                <FormControl fullWidth sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  <InputLabel>Resource Type</InputLabel>
                  <Select
                    name="type"
                    value={editResource.type}
                    onChange={handleEditChange}
                    required
                    sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  >
                    <MenuItem value=""><em>Select a type</em></MenuItem>
                    {resourceTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  value={editResource.title}
                  onChange={handleEditChange}
                  sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  required
                />
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={editResource.description}
                  onChange={handleEditChange}
                  sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  required
                />
                <TextField
                  fullWidth
                  label="Link"
                  name="link"
                  value={editResource.link}
                  onChange={handleEditChange}
                  sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  required
                />
                {editResource.type === 'worksheet' && (
                  <TextField
                    fullWidth
                    label="Solutions Link (Optional)"
                    name="solutionsLink"
                    value={editResource.solutionsLink || ''}
                    onChange={handleEditChange}
                    sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  />
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 2 },
                    justifyContent: { sm: 'flex-start' },
                  }}
                >
                  <AnimatedButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth={{ xs: true, sm: false }}
                    sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}
                  >
                    Save
                  </AnimatedButton>
                  <AnimatedButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleCloseEditModal}
                    fullWidth={{ xs: true, sm: false }}
                    sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}
                  >
                    Cancel
                  </AnimatedButton>
                </Box>
              </form>
            )}
          </ModalBox>
        </Modal>
      </Container>
    </div>
  );
};

export default ViewTopic;